import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, User, Zap, Check, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import LookupSelect from "./LookupSelect";
import AtecoInput from "./AtecoInput";
import CommittenteSelect from "./CommittenteSelect";

const EMPTY = {
  legal_name: "", website: "", sector: "", subsector: "", country: "Italia", ateco: "", company_id: null, committente: "",
  first_name: "", last_name: "", email: "", job_title: "", phone: "", seniority: "", decision_maker: false, contact_id: null,
  title: "", source: "", market: "", service: "", notes: "",
};

function Section({ icon: Icon, step, title, hint, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-center gap-2.5 mb-3">
        <span className="w-6 h-6 rounded-full bg-navy-900 text-white text-xs font-mono flex items-center justify-center">{step}</span>
        <Icon className="w-4 h-4 text-teal-600" />
        <p className="font-heading font-semibold text-sm text-slate-800">{title}</p>
        {hint && <span className="text-xs text-slate-400 ml-auto">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export default function QuickLeadDialog({ open, onOpenChange, onSaved }) {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [sugg, setSugg] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [existingContact, setExistingContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (open) { setF(EMPTY); setSugg([]); setExistingContact(null); } }, [open]);

  useEffect(() => {
    if (f.company_id || f.legal_name.trim().length < 2) { setSugg([]); return; }
    const t = setTimeout(() => {
      api.get("/leads/quick/lookup", { params: { q: f.legal_name } }).then(({ data }) => { setSugg(data.companies); setShowSugg(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [f.legal_name, f.company_id]);

  useEffect(() => {
    if (!f.email.includes("@") || f.contact_id) return;
    const t = setTimeout(() => {
      api.get("/leads/quick/lookup", { params: { email: f.email } }).then(({ data }) => {
        if (data.contact) {
          const c = data.contact;
          setExistingContact(c);
          setF((p) => ({
            ...p, contact_id: c.id, first_name: c.first_name || "", last_name: c.last_name || "", job_title: c.job_title || "",
            phone: c.phone || "", seniority: c.seniority || "", decision_maker: !!c.decision_maker,
            ...(c.company && !p.company_id ? { company_id: c.company.id, legal_name: c.company.legal_name, website: c.company.domain || "" } : {}),
          }));
        }
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [f.email, f.contact_id]);

  const pickCompany = (c) => { setF((p) => ({ ...p, company_id: c.id, legal_name: c.legal_name, website: c.domain || "" })); setShowSugg(false); };
  const clearCompany = () => setF((p) => ({ ...p, company_id: null, legal_name: "", website: "" }));
  const clearContact = () => { setExistingContact(null); setF((p) => ({ ...p, contact_id: null, email: "", first_name: "", last_name: "", job_title: "", phone: "", seniority: "", decision_maker: false })); };

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Campo obbligatorio: Titolo lead");
    if (!f.company_id && !f.legal_name.trim()) return toast.error("Campo obbligatorio: Ragione sociale");
    setSaving(true);
    try {
      const { data } = await api.post("/leads/quick", {
        company: { company_id: f.company_id, legal_name: f.legal_name, website: f.website, sector: f.sector, subsector: f.subsector, country: f.country, ateco: f.ateco },
        contact: { contact_id: f.contact_id, first_name: f.first_name, last_name: f.last_name, email: f.email, job_title: f.job_title, phone: f.phone, seniority: f.seniority, decision_maker: f.decision_maker },
        lead: { title: f.title, source: f.source, market: f.market, service: f.service, notes: f.notes, committente: f.committente },
      });
      const parts = [`Azienda ${data.created.company ? "creata" : "esistente"}`, data.contact ? `Contatto ${data.created.contact ? "creato" : "esistente"}` : "Nessun contatto"];
      toast.success(`Lead "${data.lead.title}" creato`, { description: parts.join(" · ") });
      onOpenChange(false);
      window.dispatchEvent(new Event("crm-refresh"));
      onSaved?.(data);
      if (window.location.pathname !== "/lead") nav("/lead");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-lead-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Zap className="w-5 h-5 text-teal-600" /> Lead rapido</DialogTitle>
          <DialogDescription>Un solo inserimento: azienda e persona vengono create o riutilizzate automaticamente, il lead viene agganciato a entrambe.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Section icon={Building2} step={1} title="Azienda" hint={f.company_id ? "riutilizzo azienda esistente" : "nuova se non trovata"}>
            {f.company_id ? (
              <div data-testid="quick-lead-company-chip" className="flex items-center gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-sm text-teal-800">
                <Check className="w-4 h-4" /> <span className="font-medium">{f.legal_name}</span> {f.website && <span className="text-teal-600">· {f.website}</span>}
                <button data-testid="quick-lead-company-clear" onClick={clearCompany} className="ml-auto p-1 rounded hover:bg-teal-100"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 relative">
                  <Label className="text-xs">Ragione sociale *</Label>
                  <Input data-testid="quick-lead-legal-name" value={f.legal_name} onChange={(e) => set("legal_name", e.target.value)} onFocus={() => sugg.length && setShowSugg(true)} onBlur={() => setTimeout(() => setShowSugg(false), 150)} placeholder="Es. Rossi Meccanica Srl" autoFocus />
                  {showSugg && sugg.length > 0 && (
                    <div data-testid="quick-lead-company-suggestions" className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-xl py-1">
                      <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400">Aziende già presenti</p>
                      {sugg.map((c) => (
                        <button key={c.id} data-testid={`quick-lead-suggestion-${c.id}`} onMouseDown={() => pickCompany(c)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm">
                          <Building2 className="w-4 h-4 text-teal-600" /><span className="font-medium text-slate-800">{c.legal_name}</span>
                          <span className="text-xs text-slate-400 ml-auto">{[c.domain, c.city, c.classification].filter(Boolean).join(" · ")}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div><Label className="text-xs">Sito web / dominio</Label><Input data-testid="quick-lead-website" value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="rossimeccanica.it" /></div>
                <div><Label className="text-xs">Settore</Label><LookupSelect lookup="sector" testid="quick-lead-sector" value={f.sector} onChange={(v) => set("sector", v)} /></div>
                <div><Label className="text-xs">Sottosettore</Label><LookupSelect lookup="subsector" testid="quick-lead-subsector" value={f.subsector} onChange={(v) => set("subsector", v)} /></div>
                <div><Label className="text-xs">Paese</Label><LookupSelect lookup="country" testid="quick-lead-country" value={f.country} onChange={(v) => set("country", v)} /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Codice ATECO</Label><AtecoInput testid="quick-lead-ateco" value={f.ateco} onChange={(v) => set("ateco", v)} /></div>
              </div>
            )}
          </Section>

          <Section icon={User} step={2} title="Persona" hint={existingContact ? "contatto esistente riutilizzato" : "facoltativa · dedup su email"}>
            {existingContact && (
              <div data-testid="quick-lead-contact-chip" className="flex items-center gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-sm text-teal-800 mb-3">
                <Check className="w-4 h-4" /> <span className="font-medium">{existingContact.first_name} {existingContact.last_name}</span> <span className="text-teal-600">· {f.email}</span>
                <button data-testid="quick-lead-contact-clear" onClick={clearContact} className="ml-auto p-1 rounded hover:bg-teal-100"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Email</Label><Input data-testid="quick-lead-email" type="email" value={f.email} onChange={(e) => { set("email", e.target.value); if (f.contact_id) { setExistingContact(null); set("contact_id", null); } }} placeholder="m.rossi@rossimeccanica.it" /></div>
              <div><Label className="text-xs">Nome</Label><Input data-testid="quick-lead-first-name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} disabled={!!f.contact_id} /></div>
              <div><Label className="text-xs">Cognome</Label><Input data-testid="quick-lead-last-name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} disabled={!!f.contact_id} /></div>
              <div><Label className="text-xs">Ruolo</Label><Input data-testid="quick-lead-job-title" value={f.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="Export Manager" disabled={!!f.contact_id} /></div>
              <div><Label className="text-xs">Telefono</Label><Input data-testid="quick-lead-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} disabled={!!f.contact_id} /></div>
              <div><Label className="text-xs">Seniority</Label><LookupSelect lookup="seniority" testid="quick-lead-seniority" value={f.seniority} onChange={(v) => set("seniority", v)} /></div>
              <div className="flex items-center gap-2 pt-5"><Switch data-testid="quick-lead-decision-maker" checked={f.decision_maker} onCheckedChange={(v) => set("decision_maker", v)} disabled={!!f.contact_id} /><Label className="text-xs">Decision maker</Label></div>
            </div>
          </Section>

          <Section icon={Zap} step={3} title="Lead" hint="perché questa azienda è interessante ora">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-3"><Label className="text-xs">Titolo lead *</Label><Input data-testid="quick-lead-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Es. Analisi mercato USA – form sito" /></div>
              <div><Label className="text-xs">Committente</Label><CommittenteSelect testid="quick-lead-committente" value={f.committente} onChange={(v) => set("committente", v)} /></div>
              <div><Label className="text-xs">Fonte</Label><LookupSelect lookup="source" testid="quick-lead-source" value={f.source} onChange={(v) => set("source", v)} /></div>
              <div><Label className="text-xs">Mercato</Label><LookupSelect lookup="country" testid="quick-lead-market" value={f.market} onChange={(v) => set("market", v)} /></div>
              <div><Label className="text-xs">Servizio</Label><LookupSelect lookup="service" testid="quick-lead-service" value={f.service} onChange={(v) => set("service", v)} /></div>
              <div className="sm:col-span-3"><Label className="text-xs">Note</Label><Textarea data-testid="quick-lead-notes" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            </div>
          </Section>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 [&>button]:min-h-[44px] sm:[&>button]:min-h-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quick-lead-cancel">Annulla</Button>
          <Button data-testid="quick-lead-submit" onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Crea lead
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
